<template>
  <div class="card bg-light">
    <div class="card-rounded bg-light health-data-toolbar p-3 p-lg-5">
      <div class="health-data-toolbar__row">
        <div class="health-data-toolbar__groups">
          <ul
            class="nav border-transparent fw-bold health-data-tabs"
            :class="{ 'health-data-tabs--compact': hasCompactToolbar }"
          >
            <li v-for="group in primaryGroups" :key="group.id_health_indicator_group" class="nav-item">
              <button
                type="button"
                class="btn btn-color-gray-600 btn-active-secondary btn-active-color-primary fw-bolder nav-link health-data-tab"
                :class="{ active: isSameGroup(group.id_health_indicator_group, activeGroupId) }"
                @click="onTabClick(group.id_health_indicator_group)"
              >
                {{ group.health_indicator_group_name }}
              </button>
            </li>
          </ul>
          <div
            v-if="overflowGroups.length > 0"
            ref="overflowMenu"
            class="health-data-overflow-menu flex-shrink-0"
          >
            <button
              type="button"
              class="btn btn-color-gray-600 btn-active-secondary btn-active-color-primary fw-bolder nav-link health-data-tab health-data-overflow-toggle"
              :class="{ active: isOverflowGroupActive, 'health-data-tab--compact': hasCompactToolbar }"
              @click.stop="toggleOverflowMenu"
            >
              {{ overflowDropdownLabel }}
            </button>
            <ul
              v-show="overflowMenuOpen"
              class="dropdown-menu show health-data-overflow-menu__list"
            >
              <li v-for="group in overflowGroups" :key="group.id_health_indicator_group">
                <button
                  type="button"
                  class="dropdown-item"
                  :class="{ active: isSameGroup(group.id_health_indicator_group, activeGroupId) }"
                  @click="onOverflowGroupClick(group.id_health_indicator_group)"
                >
                  {{ group.health_indicator_group_name }}
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div class="health-data-toolbar__actions">
          <button
            type="button"
            class="btn btn-icon btn-sm btn-light-primary health-data-action-btn"
            :disabled="isLoading"
            title="Ver evolução"
            data-bs-toggle="tooltip"
            @click="openEvolutionDrawer"
          >
            <i class="bi bi-graph-up-arrow"></i>
          </button>
          <button
            v-if="!readonly"
            type="button"
            class="btn btn-icon btn-sm btn-success health-data-action-btn"
            title="Adicionar"
            data-bs-toggle="modal"
            :data-bs-target="'#modal_store_patient_health_data_' + medicalRecord?.id_medrec"
            @click="currentPatientHealthData = null"
          >
            <i class="bi bi-plus-lg"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="row g-6 g-xl-9 px-5 pb-5">
      <!-- Indicador de carregamento -->
      <div v-if="isLoading" class="col-12 text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="mt-2 text-muted fs-5">Carregando dados de saúde...</p>
      </div>
      
      <!-- Dados de saúde -->
      <div v-else-if="!isLoading && patientHealthData.length > 0" v-for="healthData in patientHealthData" class="col-12">
        <div class="card border-hover-primary mb-4 health-indicator-card">
          <div class="card-header d-flex justify-content-between align-items-center health-data-header">
            <div class="d-flex align-items-center">
              <i class="bi bi-heart-pulse-fill text-primary me-2 fs-3"></i>
              <span class="fs-5 fw-bold text-dark">{{date(healthData.created_at).format('DD/MM/YYYY HH:mm')}}</span>
            </div>
            <div v-if="!readonly" class="d-flex gap-2">
              <button class="btn btn-icon btn-sm btn-light-danger" 
                      data-bs-toggle="tooltip" 
                      title="Excluir registro"
                      @click="deleteHealthData(healthData.id_patient_health_data)">
                <i class="bi bi-trash-fill fs-7"></i>
              </button>
              <button class="btn btn-icon btn-sm btn-light-primary" 
                      data-bs-toggle="modal" 
                      title="Editar registro"
                      :data-bs-target="'#modal_store_patient_health_data_' + medicalRecord?.id_medrec"
                      @click="currentPatientHealthData = healthData">
                <i class="bi bi-pencil-fill fs-7"></i>
              </button>
            </div>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div v-for="(healthIndicator, index) in healthData.health_indicators" 
                   :class="(index === healthData?.health_indicators?.length - 1 && healthData?.health_indicators?.length % 2) ? 'col-md-12' : 'col-md-6'">
                <div class="bg-light-primary bg-opacity-10 border border-primary border-opacity-20 rounded p-4 h-100 health-indicator-card">
                  <div class="d-flex align-items-center mb-3">
                    <i class="bi bi-activity text-primary me-2 fs-5"></i>
                    <span class="fw-semibold text-gray-700 fs-6">{{healthIndicator.health_indicator_name}}</span>
                  </div>
                  <div class="d-flex align-items-center">
                    <span class="fs-4 fw-bold text-primary me-2">
                      {{ formatHealthIndicatorValue(healthIndicator.patient_health_data_values.health_data_value, healthIndicator) }}
                    </span>
                    <span class="badge badge-light-primary fs-7">
                      {{healthIndicator.scale.scale_initials}}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Mensagem quando não há dados -->
      <div v-else-if="!isLoading && patientHealthData.length === 0" class="col-12 text-center py-5">
        <div class="text-muted">
          <i class="bi bi-inbox fs-1"></i>
          <p class="mt-3 fs-5">Nenhum dado de saúde encontrado para este grupo.</p>
        </div>
      </div>
    </div>
  </div>

  <HealthDataModal
      :patient-health-data="currentPatientHealthData"
      :patient-id="medicalRecord?.id_patient"
      :professional-id="medicalRecord?.id_prof"
      :healthIndicators="healthIndicators"
      :sched-id="medicalRecord?.id_sched"
      :key="medicalRecord?.id_medrec"
      :id="'modal_store_patient_health_data_' + medicalRecord?.id_medrec"
      @onClose="handleModalClose">
  </HealthDataModal>

  <HealthDataEvolutionDrawer
    :is-open="evolutionDrawerOpen"
    :group-name="currentGroupName"
    :health-indicators="healthIndicators"
    :patient-health-data="patientHealthData"
    :professional-id="schedule?.id_prof"
    :patient-id="schedule?.id_patient"
    :health-indicator-group-id="currentHealthIndicatorGroupId"
    @close="evolutionDrawerOpen = false"
  />

</template>

<script>
import http from "../../../helpers/http";
import Alert from "../../../helpers/alert";
import HealthDataModal from "../shared/HealthDataModal";
import HealthDataEvolutionDrawer from "../shared/HealthDataEvolutionDrawer";
import { formatHealthIndicatorValue } from "../../../helpers/healthDataEvolution";

const MAX_PRIMARY_TABS = 2;
const PREFERRED_PRIMARY_GROUPS = ['Sinais vitais', 'Massa corporal', 'Crescimento pediátrico'];

export default {
  name: "HealthData",
  components: {
    HealthDataModal,
    HealthDataEvolutionDrawer,
  },
  props: {
    schedule: Object,
    medicalRecord: Object,
    healthIndicatorGroups: Array,
    readonly: Boolean,
    autoLoad: {
      type: Boolean,
      default: false
    }
  },
    data() {
    return {
      activeGroupId: null,
      patientHealthData: [],
      healthIndicators: [],
      currentHealthIndicatorGroupId: '',
      currentPatientHealthData: null,
      loadedGroups: new Set(),
      isLoading: false,
      evolutionDrawerOpen: false,
      overflowMenuOpen: false,
    }
  },
  computed: {
    primaryGroups() {
      const groups = this.healthIndicatorGroups ?? [];

      if (groups.length <= MAX_PRIMARY_TABS) {
        return groups;
      }

      const preferred = PREFERRED_PRIMARY_GROUPS
        .map((name) => groups.find((group) => group.health_indicator_group_name === name))
        .filter(Boolean);

      const preferredIds = new Set(preferred.map((group) => group.id_health_indicator_group));
      const fillers = groups.filter((group) => !preferredIds.has(group.id_health_indicator_group));

      return [...preferred, ...fillers].slice(0, MAX_PRIMARY_TABS);
    },
    overflowGroups() {
      const groups = this.healthIndicatorGroups ?? [];

      if (groups.length <= MAX_PRIMARY_TABS) {
        return [];
      }

      const primaryIds = new Set(this.primaryGroups.map((group) => group.id_health_indicator_group));

      return groups.filter((group) => !primaryIds.has(group.id_health_indicator_group));
    },
    activeGroup() {
      return (this.healthIndicatorGroups ?? []).find(
        (group) => this.isSameGroup(group.id_health_indicator_group, this.activeGroupId)
      ) ?? null;
    },
    currentGroupName() {
      return this.activeGroup?.health_indicator_group_name ?? '';
    },
    isOverflowGroupActive() {
      return this.overflowGroups.some((group) => this.isSameGroup(group.id_health_indicator_group, this.activeGroupId));
    },
    overflowDropdownLabel() {
      if (this.isOverflowGroupActive) {
        return this.currentGroupName;
      }

      return 'Mais';
    },
    hasCompactToolbar() {
      return this.overflowGroups.length > 0;
    },
  },
  watch: {
    healthIndicatorGroups: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.length > 0) {
          this.setInitialActiveGroup(newVal);
          this.getHelthIndicators();

          if (this.autoLoad) {
            this.getPatientHealthData(this.activeGroupId, true);
          }
        }
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideOverflowClick);

    if (this.autoLoad && this.healthIndicatorGroups && this.healthIndicatorGroups.length > 0) {
      this.setInitialActiveGroup(this.healthIndicatorGroups);
      this.getHelthIndicators();
      this.getPatientHealthData(this.activeGroupId, true);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideOverflowClick);
  },
  methods: {
    date: moment,
    formatHealthIndicatorValue,
    isSameGroup(groupId, activeId) {
      return groupId != null && activeId != null && String(groupId) === String(activeId);
    },
    normalizeGroupId(groupId) {
      if (groupId == null || groupId === '') {
        return null;
      }

      const numericId = Number(groupId);

      return Number.isNaN(numericId) ? groupId : numericId;
    },
    setInitialActiveGroup(groups) {
      const firstPrimary = this.primaryGroups[0] ?? groups[0];

      this.activeGroupId = this.normalizeGroupId(firstPrimary?.id_health_indicator_group);
    },
    onTabClick(healthIndicatorGroupId) {
      const groupId = this.normalizeGroupId(healthIndicatorGroupId);
      this.activeGroupId = groupId;
      this.patientHealthData = [];
      this.getPatientHealthData(groupId, true);
    },
    toggleOverflowMenu() {
      this.overflowMenuOpen = !this.overflowMenuOpen;
    },
    onOverflowGroupClick(healthIndicatorGroupId) {
      this.overflowMenuOpen = false;
      this.onTabClick(healthIndicatorGroupId);
    },
    handleOutsideOverflowClick(event) {
      if (!this.overflowMenuOpen) {
        return;
      }

      const menu = this.$refs.overflowMenu;

      if (menu && !menu.contains(event.target)) {
        this.overflowMenuOpen = false;
      }
    },
    getPatientHealthData(healthIndicatorGroupId, forceReload = false) {
      const groupId = this.normalizeGroupId(healthIndicatorGroupId);
      this.currentHealthIndicatorGroupId = groupId;
      this.getHelthIndicators();
      if (this.schedule == null || groupId == null) {
        return Promise.resolve();
      }

      if (forceReload || !this.loadedGroups.has(groupId)) {
        this.isLoading = true;
        return http.get(`/professionals/${this.schedule.id_prof}/patients/${this.schedule.id_patient}/health-data`, {params:{'id_health_indicator_group': groupId}}).then((response) => {
          this.patientHealthData = response.data;
          this.loadedGroups.add(groupId);
          this.isLoading = false;
        }).catch((error) => {
          this.isLoading = false;
          console.error('Erro ao carregar dados de saúde:', error);
        });
      }

      return Promise.resolve();
    },
    getHelthIndicators() {
      return this.healthIndicators = this.activeGroup?.health_indicators ?? [];
    },
    handleModalClose() {
      // Recarrega os dados quando o modal é fechado (após adicionar/editar)
      this.loadedGroups.delete(this.currentHealthIndicatorGroupId); // Remove do cache
      this.getPatientHealthData(this.currentHealthIndicatorGroupId, true);
    },
    openEvolutionDrawer() {
      const groupId = this.activeGroupId;

      if (!groupId) {
        return;
      }

      const openDrawer = () => {
        this.evolutionDrawerOpen = true;
      };

      if (this.loadedGroups.has(groupId) && !this.isLoading) {
        openDrawer();
        return;
      }

      this.getPatientHealthData(groupId, true).then(openDrawer);
    },
    deleteHealthData (patientHealthDataId) {
      Swal.fire({
        title: 'Heeyy',
        text: "Tem certeza que deseja remover o registro?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: "btn btn-danger",
          cancelButton: 'btn btn-secundary'
        }
      }).then((result) => {
        if (result.isConfirmed) {
          http.delete(`/professionals/${this.medicalRecord.id_prof}/patients/${this.medicalRecord.id_patient}/health-data/${patientHealthDataId}`).then(() => {
            Alert.toastSuccess("Registro excluído com sucesso.");
            this.loadedGroups.delete(this.currentHealthIndicatorGroupId); // Remove do cache
            this.getPatientHealthData(this.currentHealthIndicatorGroupId, true);
          }).catch(e => {
            Alert.toastError(e.response.data.message??e.response.data);
          });
        }
      });
    },
  },
}
</script>

<style scoped>
.health-data-toolbar__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: visible;
}

.health-data-toolbar {
  overflow: visible;
}

.health-data-toolbar__groups {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex: 1 1 auto;
  min-width: 0;
  overflow: visible;
}

.health-data-toolbar__actions {
  display: flex;
  flex: 0 0 auto;
  gap: 0.35rem;
  align-items: center;
}

.health-data-tabs {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.35rem;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  padding: 0;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.health-data-tabs::-webkit-scrollbar {
  display: none;
}

.health-data-tab {
  font-size: 0.875rem;
  padding: 0.45rem 0.75rem;
  white-space: nowrap;
  line-height: 1.25;
}

.health-data-tabs--compact {
  gap: 0.3rem;
}

.health-data-tabs--compact .health-data-tab {
  font-size: 0.8125rem;
  padding: 0.4rem 0.55rem;
}

.health-data-tab--compact {
  font-size: 0.8125rem;
  padding: 0.4rem 0.55rem;
  max-width: 6.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.health-data-tab--compact.dropdown-toggle::after,
.health-data-overflow-toggle.health-data-tab--compact::after {
  margin-left: 0.2rem;
  vertical-align: 0.15em;
}

.health-data-overflow-menu {
  position: relative;
  z-index: 30;
}

.health-data-overflow-toggle::after {
  display: inline-block;
  margin-left: 0.35rem;
  vertical-align: 0.15em;
  content: '';
  border-top: 0.3em solid;
  border-right: 0.3em solid transparent;
  border-left: 0.3em solid transparent;
}

.health-data-overflow-menu__list {
  display: block;
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  min-width: 12rem;
  z-index: 1060;
  max-height: 280px;
  overflow-y: auto;
}

.health-data-action-btn.btn-icon {
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.health-data-action-btn.btn-icon i {
  font-size: 1.35rem;
  line-height: 1;
}

.health-indicator-card {
  transition: all 0.3s ease;
  border-left: 4px solid var(--bs-primary);
}

.health-indicator-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.health-data-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

@media (min-width: 992px) {
  .health-data-tab {
    font-size: 0.95rem;
    padding: 0.5rem 0.9rem;
  }

  .health-data-tabs--compact .health-data-tab {
    font-size: 0.875rem;
    padding: 0.45rem 0.7rem;
  }
}

@media (max-width: 768px) {
  .health-indicator-card {
    margin-bottom: 0.75rem;
  }

  .card-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.5rem;
  }

  .card-header .d-flex > div:last-child {
    align-self: flex-end;
  }
}

@media (max-width: 576px) {
  .row.g-3 > div {
    margin-bottom: 0.5rem;
  }
}
</style>